
<script setup>
import { ref, watch, computed } from 'vue'
import { EventBus } from '@/events/eventBus'

const props = defineProps({
  /**
   * Percentage from left (0-100)
   */
  x: {
    type: Number,
    default: 250
  },
  /**
   * Percentage from top (0-100)
   */
  y: {
    type: Number,
    default: 250
  },
  id: {
    type: String,
    default: '',
  },
  /**
   * Main text content displayed in the card body
   */
  question: {
    type: String,
    required: true
  },
  /**
   * Header label displayed next to the icon
   */
  label: {
    type: String,
    default: 'Suggested prompt'
  },
  /**
   * Visual style variant: 'suggested' (Indigo) or 'tracked' (Emerald)
   */
  variant: {
    type: String,
    default: 'suggested',
    validator: (value) => ['suggested', 'tracked'].includes(value)
  },
  loading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['track'])

const popupX = ref('center')
const popupY = ref('center')
if (props.x < 150){
  popupX.value = 'right';
}
if (props.x > 430){
  popupX.value = 'left';
}
if (props.y < 80){
  popupY.value = 'bottom';
}

const handleAction = (question, status) => {
  emit('track', question, status)
}

const transformStyle = computed(() => {
  let translateX = '-50%'
  let translateY = '-50%'

  if (popupX.value === 'left') translateX = '-80%'
  else if (popupX.value === 'right') translateX = '-20%'

  if (popupY.value === 'top') translateY = '-100%'
  else if (popupY.value === 'bottom') translateY = '0%'

  return `translate(${translateX}, ${translateY})`
})
</script>
<template>
  <div
      :id="`expanding-pill-${id}`"
    class="expanding-pill"
    :class="[`variant-${variant}`]"
    :style="{ top: `${y}px`, left: `${x}px`, transform: transformStyle }"
    @mouseenter="EventBus.emit('onIconAnnotationHover', { label: question, x, y })"
    @mouseleave="EventBus.emit('onIconAnnotationLeave', { label: question, x, y })"
  >
    <!-- 1. Icon Layer -->
    <div class="icon-box">
      <!-- Suggested Icon (Question Mark) -->
      <span class="fa fa-solid fa-plus"></span>
    </div>

    <!-- 2. Header Layer -->
    <div class="header-group">
      <span class="label-text">{{ label }}</span>
    </div>

    <!-- 3. Content Layer (Visible on Hover) -->
    <div class="content-body">
      <p class="question-text">
        {{ question }}
      </p>

      <div class="action-row">
        <!-- Actions for Suggested State -->
        <template v-if="variant === 'suggested'">
          <button class="btn btn-primary" :disabled="loading" @click.stop="handleAction(question, 'active')">
            <span v-if="loading" class="fa fa-spinner fa-spin me-1"></span>
            <span v-else class="fa fa-plus me-1"></span>
            Track
          </button>
          <button class="btn btn-secondary" :disabled="loading" @click.stop="handleAction(question, 'ignored')">
            Ignore
          </button>
        </template>

        <!-- Actions for Tracked State -->
        <template v-else>
          <button class="btn btn-primary btn-danger" :disabled="loading" @click.stop="handleAction(question, 'ignored')">
            <span v-if="loading" class="fa fa-spinner fa-spin me-1"></span>
            <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="me-1">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
            Untrack Question
          </button>
        </template>
      </div>
    </div>
  </div>
</template>


<style scoped>
/* --- Component Styles --- */

.expanding-pill {
  /* Dynamic Theme Variables (Default to Indigo/Suggested) */
  --ep-theme-primary: #4f46e5;       /* Indigo 600 */
  --ep-theme-bg: #eef2ff;            /* Indigo 50 */
  --ep-theme-border: #e0e7ff;        /* Indigo 100 */
  --ep-transition-spring: all 0.5s cubic-bezier(0.32, 0.72, 0, 1);
  --ep-color-text-main: #1e293b;
  --ep-color-text-muted: #64748b;
  --ep-color-white: #ffffff;

  /* Reset box model for the component */
  box-sizing: border-box;
  white-space: normal;

  position: absolute;
  background: var(--ep-color-white);
  border: 1px solid var(--ep-theme-border);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border-radius: 8px;

  /* Initial State: Square Icon Only */
  width: 32px;
  height: 32px;
  max-height: 32px; /* Keep a strict cap for a smooth expand */
  overflow: hidden;

  cursor: default;
  transition: var(--ep-transition-spring);
  z-index: 1; /* Base z-index */
  user-select: text;

  opacity: 1;
}


/* Ensure children inherit box-sizing */
.expanding-pill *,
.expanding-pill *::before,
.expanding-pill *::after {
  box-sizing: inherit;
}

/* --- Variant: Tracked (Emerald Theme) --- */
.expanding-pill.variant-tracked {
  --ep-theme-primary: #10b981;       /* Emerald 500 */
  --ep-theme-bg: #ecfdf5;            /* Emerald 50 */
  --ep-theme-border: #d1fae5;        /* Emerald 100 */
}


/* --- Hover Expansion --- */
.expanding-pill:hover {
  opacity:1;
  width: 260px;
  min-height: 32px;
  height: auto;
  max-height: 1000px; /* Allow long questions to expand smoothly */
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  border-color: var(--ep-theme-border);
  z-index: 50; /* Bring to front on hover */

  /* Reset animation on hover */
  animation: none;
  overflow: hidden; /* Prevent overflow from snapping in before the max-height animates */
  transition: var(--ep-transition-spring), max-height 0.5s ease-in-out;
}

.expanding-pill:hover::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--ep-color-white);
  border-radius: 8px;
  z-index: -1;
  border: 1px solid var(--ep-theme-border);
  //pointer-events: none; /* Ensure pseudo-element doesn't block clicks */
}

.expanding-pill:hover .header-group,
.expanding-pill:hover .content-body {
  position: relative;
  z-index: 2; /* Increased z-index to be above ::before and icon-box */
}

/* --- 1. Icon Layer --- */
.icon-box {
  position: absolute;
  background-color: var(--ep-theme-bg);
  color: var(--ep-theme-primary);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: var(--ep-transition-spring);
  cursor: pointer;

  /* Initial Position: Centered in Square */
  top: 2px;
  left: 2px;
  width: 26px;
  height: 26px;

}

.expanding-pill:hover .icon-box {
  /* Expanded Position: Top Left */
  top: 8px;
  left: 8px;
  width: 28px;
  height: 28px;
  z-index: 1; /* Below text but above background */
  animation: none; /* Disable animation when expanded */
}

/* --- 2. Header Layer (Hidden initially) --- */
.header-group {
  display: flex;
  align-items: center;
  transition: var(--ep-transition-spring);
  padding: 8px 12px 0 12px;
  height: 24px;
  opacity: 0;
  //pointer-events: none;
}

.expanding-pill:hover .header-group {
  /* Expanded Position: Visible */
  opacity: 1;
  //pointer-events: auto;
  justify-content: center;
  transition-delay: 0.1s;
}

.label-text {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--ep-color-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1;
  text-align: center;
  flex: 1;
}

/* --- 3. Content Layer --- */
.content-body {
  padding: 16px 6px 6px 6px;
  opacity: 0;
  transform: translateY(8px);
  transition: opacity 0.3s ease, transform 0.3s ease;
  //pointer-events: none;
  text-align: center;
}

.expanding-pill:hover .content-body {
  opacity: 1;
  transform: translateY(0);
  transition: opacity 0.4s ease, transform 0.4s ease;
  transition-delay: 0.15s;
  //pointer-events: auto;
}

.question-text {
  font-size: 13px;
  font-weight: 500;
  color: var(--ep-color-text-main);
  margin: 0 0 10px 0;
  line-height: 1.4;
  cursor: text;
}

.action-row {
  display: flex;
  gap: 6px;
  justify-content: center;
}

.action-row .btn {
  padding: 4px 10px;
  font-size: 12px;
  height: auto;
}


</style>
